import { useSelector } from "react-redux";
import { type RootState } from "../../../redux/store";
import { selectProductResultAmount } from "~/Search/redux/searchSelectors";
import { selectProductDescriptor } from "~/redux/products/productsSelectors";
import { selectNavigationBreadcrumbs } from "~/jspComponents/Breadcrumbs/redux/selectors";
import { ImageFormat } from "../../../dto/ProductDataDTO";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { type PdpPageProductData } from "~/ProductDetails/models/PdpPageProductData";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { resolveUrl } from "../../../utils/url/urlResolver";
import { getPrimaryImage } from "~/utils/product/getPrimaryImage";

type JsonLdBreadcrumb = {
  "@type": "ListItem";
  position: number;
  item: {
    "@id": string;
    name: string;
  };
};

export const PLPJsonLDComponent = () => {
  const navigationBreadcrumbs = useSelector(selectNavigationBreadcrumbs);
  const totalNumberOfResults = useSelector(selectProductResultAmount);

  const productCodes = useSelector((state: RootState) =>
    state.srp.results.results.slice(0, 20).map((row) => row.productCode),
  );
  const productDescriptorList: ProductDescriptor<PdpPageProductData>[] = useSelector((state: RootState) => {
    return productCodes.map(
      (productCode) =>
        selectProductDescriptor(state, {
          productCode,
          priceReference: getInitialPriceReference(),
        }) as ProductDescriptor<PdpPageProductData>,
    );
  });

  const getBreadcrumbList = () => {
    const base = [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@id": window.location.origin + resolveUrl(HOMEPAGE),
          name: "Home",
        },
      },
    ];

    const dynamicBreadcrumbs = navigationBreadcrumbs?.reduce((acc, crumb, idx) => {
      acc.push({
        "@type": "ListItem",
        position: idx + 2,
        item: {
          "@id": window.location.origin + resolveUrl(crumb.url),
          name: crumb.name,
        },
      });
      return acc;
    }, [] as JsonLdBreadcrumb[]);

    return [...base, ...(dynamicBreadcrumbs || [])];
  };

  const getProductList = () => {
    return productDescriptorList
      .map((item) => {
        const product = item?.base;
        if (!product) {
          return null;
        }

        const brand = product.manufacturerLogoUrl?.includes("FAG")
          ? "FAG"
          : product.manufacturerLogoUrl?.includes("INA")
          ? "INA"
          : "";

        const image = getPrimaryImage(product, { imageFormat: ImageFormat.product });
        const pdpUrl = getProductDetailsUrl(product);

        return {
          "@type": "Product",
          image: image?.url,
          url: pdpUrl,
          name: product.designation,
          brand: {
            "@type": "Brand",
            name: "Schaeffler Medias",
          },
          manufacturer: {
            "@type": "Organization",
            name: brand,
          },
        };
      })
      .filter(Boolean);
  };

  const currentPageUrl = () => {
    const lastBreadcrumb = navigationBreadcrumbs?.[navigationBreadcrumbs.length - 1];
    const rawUrl = lastBreadcrumb?.url || "/";
    return new URL(rawUrl.startsWith("/") ? window.location.origin + rawUrl : rawUrl).toString();
  };

  const schemaData = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: getBreadcrumbList(),
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      url: currentPageUrl(),
      numberOfItems: totalNumberOfResults,
      itemListElement: getProductList(),
    },
  ];

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />;
};

const getProductDetailsUrl = (productData: PdpPageProductData) => {
  const url = resolveUrl(productData.url);
  if (!url) {
    return url;
  }
  const urlObj = new URL(url.startsWith("/") ? window.location.origin + url : url);
  return urlObj.toString();
};
